/**
 * serviceComponentHole 共享 canvas/坐标基础设施
 *
 * 职责：
 *  - 参数校验
 *  - 加载图片信息、计算 mm→px 比例
 *  - 计算 canvas 物理/逻辑尺寸、留白、ctx 偏移
 *  - 计算 outline 在 canvas 坐标系下的点集
 *  - canvas ↔ 后端坐标互转
 *
 * 不绑定具体 type；type 的几何（半径/矩形等）由各 types/*.js 自行换算。
 */

import { DRAW_TYPE, DEFAULT_CONFIG, lineColor } from './constants.js';
import * as mwx from '@/utils/common.js';

/**
 * 加载图片元素（含跨域）。
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise(function (resolve, reject) {
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function () {
      reject(new Error('图片加载失败: ' + url));
    };
    img.src = url;
  });
}

/**
 * 通过原生 Image 拿到图片的 width / height（避免每次 drawImage 再去解析）。
 * @param {string} url
 * @returns {Promise<{width:number,height:number}>}
 */
export async function getImageInfo(url) {
  const img = await loadImage(url);
  return { width: img.width, height: img.height };
}

/**
 * 计算 mask 白色区域在整张图中的撑满倍数（与 serviceHoleNew/core.js 一致）。
 * 影响所有 type 的 mm→px 实际半径/宽高。
 * @param {string} mask
 * @returns {Promise<number>}
 */
async function computeMaskFitScale(mask) {
  if (!mask) {
    return 1;
  }
  try {
    const img = await loadImage(mask);
    if (!img.width || !img.height) {
      return 1;
    }
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const data = imageData.data;
    let minX = img.width;
    let minY = img.height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < img.height; y++) {
      for (let x = 0; x < img.width; x++) {
        const idx = (y * img.width + x) * 4;
        const isWhite =
          data[idx + 3] > 0 &&
          data[idx] >= 240 &&
          data[idx + 1] >= 240 &&
          data[idx + 2] >= 240;
        if (!isWhite) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    canvas.width = 0;
    canvas.height = 0;
    if (maxX === -1 || maxY === -1) return 1;
    const rectW = maxX - minX + 1;
    const rectH = maxY - minY + 1;
    if (!rectW || !rectH) return 1;
    const rectRatio = rectW / rectH;
    const maskRatio = img.width / img.height;
    const scale =
      rectRatio > maskRatio ? img.width / rectW : img.height / rectH;
    if (!isFinite(scale) || scale <= 0) return 1;
    return scale < 1 ? 1 : scale;
  } catch (error) {
    console.warn('[serviceComponentHole] 计算 maskFitScale 失败', error);
    return 1;
  }
}

/**
 * 把 outlinePoints 按 y 中位数切分上下两半，供 keyHole 默认上居中 / 下居中选点用。
 * 与 serviceHoleNew/core.js#findMedianYIndex 行为一致。
 * @param {Array<Array<number>>} pointsCanvas - canvas 坐标系点集
 * @returns {{topHalfYArr:Array<Array<number>>, bottomHalfYArr:Array<Array<number>>}}
 */
function splitByMedianY(pointsCanvas) {
  if (!pointsCanvas || !pointsCanvas.length) {
    return { topHalfYArr: [], bottomHalfYArr: [] };
  }
  const sorted = [...pointsCanvas].sort((a, b) => a[1] - b[1]);
  const n = sorted.length;
  const medianY =
    n % 2 === 1 ? sorted[Math.floor(n / 2)][1] : sorted[n / 2 - 1][1];
  const idx = sorted.findIndex((p) => p[1] === medianY);
  return {
    topHalfYArr: sorted.slice(0, idx),
    bottomHalfYArr: sorted.slice(idx + 1)
  };
}

/**
 * 入参基本校验。
 * @param {Object} args
 * @param {string} args.imageSrc
 * @param {Array} args.outline
 * @throws {Error}
 */
export function validateInitParams({ imageSrc, outline }) {
  if (!imageSrc) {
    throw new Error('[serviceComponentHole] imageSrc 为必填参数');
  }
  if (!outline || !Array.isArray(outline)) {
    throw new Error('[serviceComponentHole] outline 必须是数组');
  }
}

/**
 * 获取钥匙扣预设中的最大外探绘制距离（外圆半径 + 内圆半径）。
 * @param {Object} sizePresets - 页面/API 注入的规格预设表
 * @returns {number}
 */
function getMaxKeyHoleReachMm(sizePresets) {
  const list = (sizePresets && sizePresets[DRAW_TYPE.KEY_HOLE]) || [];
  let max = 0;
  for (let i = 0; i < list.length; i++) {
    const geometryConfig = list[i] && list[i].geometryConfig;
    const outerHoleDiameterMm =
      geometryConfig && geometryConfig.outerHoleDiameterMm;
    const innerHoleDiameterMm =
      geometryConfig && geometryConfig.innerHoleDiameterMm;
    if (
      typeof outerHoleDiameterMm === 'number' &&
      typeof innerHoleDiameterMm === 'number' &&
      outerHoleDiameterMm > 0 &&
      innerHoleDiameterMm > 0
    ) {
      const reachMm = outerHoleDiameterMm / 2 + innerHoleDiameterMm / 2;
      if (reachMm > max) {
        max = reachMm;
      }
    }
  }
  return max;
}

/**
 * 计算"轮廓四周留白比例"（mm 域）。
 *
 * 多部件页阶段：沿用旧 serviceHoleNew 的钥匙扣留白逻辑（外径+内径 / 基准边）
 * 作为基线；同时按可选钥匙扣中的最大外探绘制距离计算单边留白，并取两者较大值。
 *
 * @param {Object} args
 * @param {Object} args.template
 * @param {number} args.baseMM
 * @param {Object} args.sizePresets
 * @returns {number}
 */
function computeBaseWhiteSpacePercent({ template, baseMM, sizePresets }) {
  const KEY_HOLE_OUTER_MM = 6;
  const KEY_HOLE_INNER_MM = 2.5;
  const LIPAI_HEIGHT_MM = template.slotHeight || 2.4;
  const legacyWhiteSpacePercent = template.isLiPai
    ? (LIPAI_HEIGHT_MM * 2) / baseMM
    : (KEY_HOLE_OUTER_MM / 2 + KEY_HOLE_INNER_MM / 2) / baseMM;
  const maxKeyHoleReachMm = getMaxKeyHoleReachMm(sizePresets);
  const keyHoleWhiteSpacePercent = maxKeyHoleReachMm
    ? maxKeyHoleReachMm / baseMM
    : 0;
  // 留白不低于旧逻辑，也不低于最大钥匙扣外探绘制距离。
  return Math.max(legacyWhiteSpacePercent, keyHoleWhiteSpacePercent);
}

/**
 * 初始化共享 canvas 状态（不创建任何组件实例）。
 *
 * @param {Object} args
 * @param {string} args.imageSrc
 * @param {Array<Array<number>>} args.outline - 后端坐标系
 * @param {number} args.erode
 * @param {string} args.mask
 * @param {HTMLCanvasElement} args.canvas
 * @param {HTMLCanvasElement} [args.scaleCanvas]
 * @param {number} args.maxCanvasWidth
 * @param {number} args.maxCanvasHeight
 * @param {Object} args.template - 来自 Store 的 skuTemplate
 * @param {string} args.mode - 'edit' | 'draw'
 * @param {number} args.strokeWidth
 * @param {Object} [args.imageBorderParseParams] - 立牌场景 imageBorderParse 入参
 * @param {Array<number>} [args.imageBorderParseRect] - imageBorderParse 返回的 rect，格式为 [x, y, width, height]
 * @param {Object} args.sizePresets - 页面/API 注入的规格预设表
 * @returns {Promise<Object>} sharedState
 *
 * @example
 * const sharedState = await initSharedCanvasState({
 *   imageSrc, outline, erode, mask, canvas, scaleCanvas,
 *   maxCanvasWidth: 750, maxCanvasHeight: 1000,
 *   template, mode: 'edit', strokeWidth: 2, debug: false
 * });
 */
export async function initSharedCanvasState({
  imageSrc,
  outline,
  erode,
  mask,
  canvas,
  scaleCanvas,
  maxCanvasWidth = 2000,
  maxCanvasHeight = 2000,
  template,
  mode,
  strokeWidth,
  imageBorderParseParams,
  imageBorderParseRect,
  sizePresets,
  isCustomSkuSize,
  debug
}) {
  const sharedState = {
    imageSrc,
    outline,
    erode: erode || 0,
    strokeWidth,
    lineColor,
    mask,
    template,
    imageBorderParseParams: imageBorderParseParams || null,
    imageBorderParseRect: imageBorderParseRect || null,
    baseImageBorderParseRect: imageBorderParseRect || null,
    canvas,
    scaleCanvas,
    ctx: null,
    mode: mode || 'edit',
    isCustomSkuSize: !!isCustomSkuSize,
    debug: !!debug,
    destroyed: false,
    boundEvents: []
  };

  if (!canvas) {
    sharedState.canvas = document.createElement('canvas');
  }
  sharedState.ctx = sharedState.canvas.getContext('2d');

  const imageInfo = await getImageInfo(imageSrc);
  sharedState.imageInfo = imageInfo;
  sharedState.maskFitScale = await computeMaskFitScale(mask);

  // --- 阶段 1：mm 基准边 ---
  const imageRatioOver =
    imageInfo.width / imageInfo.height >
    template.paperWidth / template.paperHeight;
  sharedState.imageRatioOver = imageRatioOver;
  // 自定义尺寸场景中比例不一致只做诊断，不阻断后续坐标扩展逻辑。
  if (sharedState.isCustomSkuSize) {
    const imageRatio = imageInfo.width / imageInfo.height;
    const skuRatio = template.paperWidth / template.paperHeight;
    if (Math.abs(imageRatio - skuRatio) > 0.02) {
      console.error(
        '[serviceComponentHole] isCustomSkuSize 图片比例与 SKU 比例不一致',
        {
          imageWidth: imageInfo.width,
          imageHeight: imageInfo.height,
          paperWidth: template.paperWidth,
          paperHeight: template.paperHeight
        }
      );
    }
  }
  const skuTemplateBaseMM = imageRatioOver
    ? template.paperWidth
    : template.paperHeight;
  sharedState.skuTemplateBaseMM = skuTemplateBaseMM;

  // --- 阶段 2：留白比例（mm 域）---
  const baseWhiteSpacePercent = computeBaseWhiteSpacePercent({
    template,
    baseMM: skuTemplateBaseMM,
    sizePresets
  });

  // --- 阶段 3：canvas 逻辑尺寸 ---
  const whiteSpaceBasePx = imageRatioOver ? imageInfo.width : imageInfo.height;
  const originRoundWhiteSpace =
    baseWhiteSpacePercent *
    whiteSpaceBasePx *
    DEFAULT_CONFIG.canvasWhiteSpaceSafeRatio;
  let canvasWidth = imageInfo.width + originRoundWhiteSpace * 2;
  let canvasHeight = imageInfo.height + originRoundWhiteSpace * 2;
  const canvasRatio = canvasWidth / canvasHeight;
  const originCanvasWidth = canvasWidth;

  if (canvasWidth > maxCanvasWidth || canvasHeight > maxCanvasHeight) {
    const boundRatio = maxCanvasWidth / maxCanvasHeight;
    if (boundRatio > canvasRatio) {
      canvasHeight = maxCanvasHeight;
      canvasWidth = canvasHeight * canvasRatio;
    } else {
      canvasWidth = maxCanvasWidth;
      canvasHeight = canvasWidth / canvasRatio;
    }
  }
  sharedState.canvasWidth = canvasWidth;
  sharedState.canvasHeight = canvasHeight;

  const dpr = DEFAULT_CONFIG.dpr;
  sharedState.dpr = dpr;
  sharedState.canvas.width = canvasWidth * dpr;
  sharedState.canvas.height = canvasHeight * dpr;
  sharedState.canvas.style.width = canvasWidth + 'px';
  sharedState.canvas.style.height = canvasHeight + 'px';

  // --- 阶段 4：核心缩放比 ---
  const canvasScale = canvasWidth / originCanvasWidth;
  sharedState.canvasScale = canvasScale;
  const imageScale =
    1 - sharedState.erode / Math.min(imageInfo.width, imageInfo.height);
  sharedState.imageScale = imageScale;
  sharedState.drawImageWidth = imageInfo.width * imageScale * canvasScale;
  sharedState.drawImageHeight = imageInfo.height * imageScale * canvasScale;
  sharedState.canvasRoundWhiteSpace = originRoundWhiteSpace * canvasScale;

  // --- 阶段 5：outline 坐标换算到 canvas 系 ---
  sharedState.outlinePoints = outline.map((p) => [
    Math.round(p[0] * canvasScale),
    Math.round(p[1] * canvasScale)
  ]);
  sharedState.originOutlinePoints = sharedState.outlinePoints.map((p) => [
    p[0],
    p[1]
  ]);
  const median = splitByMedianY(sharedState.outlinePoints);
  sharedState.topHalfYArr = median.topHalfYArr;
  sharedState.bottomHalfYArr = median.bottomHalfYArr;

  // 线宽（与旧实现一致）
  let lineWidth = Math.max((Math.max(canvasWidth, canvasHeight) / 200) * 1, 1);
  lineWidth = Math.max(Math.round(lineWidth / sharedState.maskFitScale), 1);
  sharedState.lineWidth = lineWidth;
  sharedState.ctx.lineWidth = lineWidth;

  // --- 阶段 6：mm→px ---
  const skuTemplatePx =
    (imageRatioOver ? imageInfo.width : imageInfo.height) * canvasScale;
  sharedState.mmToPixelRatio = skuTemplatePx / skuTemplateBaseMM;

  // --- 阶段 7：图片边界（canvas 系，已减留白偏移）---
  sharedState.imageLeft =
    (canvasWidth - sharedState.drawImageWidth) / 2 -
    sharedState.canvasRoundWhiteSpace;
  sharedState.imageRight = sharedState.imageLeft + sharedState.drawImageWidth;
  sharedState.imageTop =
    (canvasHeight - sharedState.drawImageHeight) / 2 -
    sharedState.canvasRoundWhiteSpace;
  sharedState.imageBottom = sharedState.imageTop + sharedState.drawImageHeight;

  // --- 阶段 8：ctx 偏移到留白内边（tx/ty 需乘 dpr，使 CSS 坐标 (0,0) 映射到屏幕 CSS 位置 canvasRoundWhiteSpace）
  sharedState.ctx.setTransform(
    dpr,
    0,
    0,
    dpr,
    sharedState.canvasRoundWhiteSpace * dpr,
    sharedState.canvasRoundWhiteSpace * dpr
  );

  // scaleCanvas 初始化（放大镜）
  if (scaleCanvas) {
    const size = scaleCanvas.clientWidth;
    scaleCanvas.width = size;
    scaleCanvas.height = size;
    scaleCanvas.style.width = size + 'px';
    scaleCanvas.style.height = size + 'px';
  }

  return sharedState;
}

/**
 * 将 imageBorderParse 返回的 rect 转为 outlineBounds 数据结构。
 * @param {Array<number>} rect - imageBorderParse 返回的 rect，格式为 [x, y, width, height]
 * @returns {{x:number,y:number,width:number,height:number}|null} outlineBounds 矩形
 */
function imageBorderParseRectToOutlineBounds(rect) {
  // rect 必须包含 x/y/width/height 四个有效数值。
  if (!Array.isArray(rect) || rect.length < 4) {
    return null;
  }
  const x = Number(rect[0]);
  const y = Number(rect[1]);
  const width = Number(rect[2]);
  const height = Number(rect[3]);
  // 矩形宽高无效时不能继续同步自定义尺寸。
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !(width > 0) ||
    !(height > 0)
  ) {
    return null;
  }
  return {
    x,
    y,
    width,
    height
  };
}

/**
 * 校验描边原图比例是否与纸张比例一致。
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {void}
 */
function validateCustomSkuImagePaperRatio(sharedState) {
  const imageWidth = Number(
    sharedState.imageInfo && sharedState.imageInfo.width
  );
  const imageHeight = Number(
    sharedState.imageInfo && sharedState.imageInfo.height
  );
  const params = sharedState.imageBorderParseParams || {};
  const paperWidth = Number(
    params.paperWidth != null
      ? params.paperWidth
      : sharedState.template && sharedState.template.paperWidth
  );
  const paperHeight = Number(
    params.paperHeight != null
      ? params.paperHeight
      : sharedState.template && sharedState.template.paperHeight
  );
  // 缺少宽高基准时无法验证 H = W * R。
  if (!imageWidth || !imageHeight || !paperWidth || !paperHeight) {
    throw new Error('自定义尺寸同步失败：缺少描边原图或纸张宽高');
  }
  const expectedImageHeight = imageWidth * (paperHeight / paperWidth);
  const tolerance = Math.max(1, imageHeight * 0.001);
  // 描边原图比例必须和纸张比例一致，否则 rect 与纸张尺寸无法直接换算。
  if (Math.abs(imageHeight - expectedImageHeight) > tolerance) {
    mwx.log.error('[serviceComponentHole] 描边原图与纸张比例校验失败', {
      paperWidth,
      paperHeight,
      imageWidth,
      imageHeight,
      expectedImageHeight,
      imageHeightDiff: imageHeight - expectedImageHeight,
      tolerance
    });
    // 非开发环境只记录误差日志，避免调试校验中断用户流程。
    if (!mwx.isDev) {
      return;
    }
    throw new Error('自定义尺寸同步失败：描边原图宽高比例与纸张比例不一致');
  }
}

/**
 * canvas 坐标 → 后端坐标。与 serviceHoleNew/core.js#convertCanvasToBackend 行为一致。
 * @param {{x:number,y:number}} point
 * @param {Object} sharedState
 * @returns {{x:number,y:number}}
 */
export function convertCanvasToBackend(point, sharedState) {
  const imageRatio = sharedState.imageInfo.width / sharedState.imageInfo.height;
  const skuRatio =
    sharedState.template.paperWidth / sharedState.template.paperHeight;
  let offsetX = 0;
  let offsetY = 0;
  if (imageRatio > skuRatio) {
    offsetY =
      (sharedState.imageInfo.width / skuRatio - sharedState.imageInfo.height) /
      2;
  } else {
    offsetX =
      (sharedState.imageInfo.height * skuRatio - sharedState.imageInfo.width) /
      2;
  }
  return {
    x: Math.round(point.x / sharedState.canvasScale + offsetX),
    y: Math.round(point.y / sharedState.canvasScale + offsetY)
  };
}

/**
 * 创建空的后端坐标边界。
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}}
 */
function createBackendBounds() {
  return {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity
  };
}

/**
 * 将后端坐标点合并进边界。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @param {{x:number,y:number}} point - 后端坐标点
 * @returns {void}
 */
function mergeBackendPoint(bounds, point) {
  bounds.minX = Math.min(bounds.minX, point.x);
  bounds.minY = Math.min(bounds.minY, point.y);
  bounds.maxX = Math.max(bounds.maxX, point.x);
  bounds.maxY = Math.max(bounds.maxY, point.y);
}

/**
 * 判断后端坐标边界是否有效。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @returns {boolean}
 */
function isValidBackendBounds(bounds) {
  return (
    isFinite(bounds.minX) &&
    isFinite(bounds.minY) &&
    isFinite(bounds.maxX) &&
    isFinite(bounds.maxY)
  );
}

/**
 * 将后端坐标边界转成矩形。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @returns {{x:number,y:number,width:number,height:number}|null}
 */
function backendBoundsToRect(bounds) {
  // 边界无效时返回空值，避免尺寸比例被非法数字污染。
  if (!isValidBackendBounds(bounds)) {
    return null;
  }
  return {
    x: bounds.minX,
    y: bounds.minY,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY
  };
}

/**
 * 将 canvas 坐标刀线点合并进后端坐标边界，同时返回仅刀线点集的独立边界供增量计算。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @param {Array<Array<number>>} outlinePoints - canvas 坐标刀线点
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null} 仅刀线点集的后端坐标边界
 */
function mergeOutlinePointsToBackendBounds(bounds, outlinePoints, sharedState) {
  const points = outlinePoints || [];
  // 记录刀线在后端坐标系下的独立边界及四个极值点。
  var outlineBounds = createBackendBounds();
  var topPoint = null;
  var bottomPoint = null;
  var leftPoint = null;
  var rightPoint = null;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    // 刀线点格式异常时跳过该点，避免单个坏点污染整体矩形。
    if (!p || !isFinite(p[0]) || !isFinite(p[1])) {
      continue;
    }
    var backendPt = convertCanvasToBackend({ x: p[0], y: p[1] }, sharedState);
    // 跟踪四个极值点（后端坐标系）。
    if (!topPoint || backendPt.y < topPoint.y)
      topPoint = { x: backendPt.x, y: backendPt.y };
    if (!bottomPoint || backendPt.y > bottomPoint.y)
      bottomPoint = { x: backendPt.x, y: backendPt.y };
    if (!leftPoint || backendPt.x < leftPoint.x)
      leftPoint = { x: backendPt.x, y: backendPt.y };
    if (!rightPoint || backendPt.x > rightPoint.x)
      rightPoint = { x: backendPt.x, y: backendPt.y };
    mergeBackendPoint(bounds, backendPt);
    mergeBackendPoint(outlineBounds, backendPt);
  }
  if (isValidBackendBounds(outlineBounds)) {
    console.log(
      '[纯刀线区域] 宽高:',
      {
        width: outlineBounds.maxX - outlineBounds.minX + 1,
        height: outlineBounds.maxY - outlineBounds.minY + 1
      },
      '四极值点:',
      {
        上: topPoint,
        下: bottomPoint,
        左: leftPoint,
        右: rightPoint
      }
    );
  }
  return isValidBackendBounds(outlineBounds) ? outlineBounds : null;
}

/**
 * 将孔位区域合并进后端坐标边界。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @param {Array<Object>} holeRegions - 当前孔位区域
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {void}
 */
function mergeHoleRegionsToBackendBounds(bounds, holeRegions, sharedState) {
  const list = holeRegions || [];
  const k = sharedState.canvasScale || 1;
  for (let i = 0; i < list.length; i++) {
    const region = list[i];
    // 缺少中心点的孔位无法换算后端坐标，跳过该区域。
    if (!region || !isFinite(region.cx) || !isFinite(region.cy)) {
      continue;
    }
    const center = convertCanvasToBackend(
      { x: region.cx, y: region.cy },
      sharedState
    );
    if (region.kind === 'rotatedRect') {
      const rect = getBackendRotatedRectAabb(
        center,
        (region.w || 0) / k,
        (region.h || 0) / k,
        region.rotationRad || 0
      );
      mergeBackendRect(bounds, rect);
      continue;
    }
    // 圆形孔位按半径外接矩形参与最终尺寸外扩。
    if (region.radius != null) {
      const backendRadius = region.radius / k;
      mergeBackendRect(bounds, {
        left: center.x - backendRadius,
        top: center.y - backendRadius,
        right: center.x + backendRadius,
        bottom: center.y + backendRadius
      });
    }
  }
}

/**
 * 计算组件业务坐标还原缩放比。
 * @param {Object} savedInfo - 保存的组件 origin 信息
 * @param {number} savedInfo.baseCanvasWidth - 保存时包含留白的 canvas 宽度
 * @param {number} savedInfo.canvasRoundWhiteSpace - 保存时单边留白宽度
 * @param {Object} sharedState - 当前共享状态
 * @returns {number}
 */
export function getRestoreBusinessScale(savedInfo, sharedState) {
  // 恢复缩放依赖历史留白宽度，缺失或非法时直接报错，避免生成 NaN 坐标。
  if (
    !savedInfo ||
    !isFinite(savedInfo.baseCanvasWidth) ||
    !isFinite(savedInfo.canvasRoundWhiteSpace) ||
    !sharedState ||
    !isFinite(sharedState.canvasWidth) ||
    !isFinite(sharedState.canvasRoundWhiteSpace)
  ) {
    throw new Error('[serviceComponentHole] restore 缩放参数非法');
  }
  const oldBusinessWidth =
    savedInfo.baseCanvasWidth - savedInfo.canvasRoundWhiteSpace * 2;
  const currentBusinessWidth =
    sharedState.canvasWidth - sharedState.canvasRoundWhiteSpace * 2;
  // 业务宽度必须为有效正数，否则无法按比例还原组件坐标。
  if (
    !isFinite(oldBusinessWidth) ||
    oldBusinessWidth <= 0 ||
    !isFinite(currentBusinessWidth) ||
    currentBusinessWidth <= 0
  ) {
    throw new Error('[serviceComponentHole] restore 业务宽度非法');
  }
  return currentBusinessWidth / oldBusinessWidth;
}

/**
 * 把 touch / mouse 事件坐标转 canvas 内坐标（已减留白偏移）。
 * @param {Event} e
 * @param {Object} sharedState
 * @returns {{x:number,y:number}}
 */
export function getCanvasPoint(e, sharedState) {
  let clientX = e.clientX;
  let clientY = e.clientY;
  if (e.touches && e.touches[0]) {
    clientX = e.touches[0].clientX;
    clientY = e.touches[0].clientY;
  } else if (e.changedTouches && e.changedTouches[0]) {
    clientX = e.changedTouches[0].clientX;
    clientY = e.changedTouches[0].clientY;
  }
  const rect = sharedState.canvas.getBoundingClientRect();
  return {
    x: clientX - rect.left - sharedState.canvasRoundWhiteSpace,
    y: clientY - rect.top - sharedState.canvasRoundWhiteSpace
  };
}

/**
 * 射线法 + epsilon 容差判断点是否在多边形内。
 * 用于 type.clampSelf 判定圆心是否在 outline 内。
 * @param {{x:number,y:number}} point
 * @param {Array<Array<number>>} polygon
 * @returns {boolean}
 */
export function isPointInPolygon(point, polygon) {
  const x = point.x;
  const y = point.y;
  let inside = false;
  const eps = 1e-9;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    if (Math.abs(x - xi) < eps && Math.abs(y - yi) < eps) return true;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi + eps;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * 找到距离 point 最近的轮廓点。
 * @param {{x:number,y:number}} point
 * @param {Array<Array<number>>} pointsArray
 * @returns {{x:number,y:number}|null}
 */
export function findClosestPoint(point, pointsArray) {
  let minSq = Infinity;
  let closest = null;
  for (const p of pointsArray) {
    const dx = p[0] - point.x;
    const dy = p[1] - point.y;
    const sq = dx * dx + dy * dy;
    if (sq < minSq) {
      minSq = sq;
      closest = { x: p[0], y: p[1] };
    }
  }
  return closest;
}

/**
 * 将 canvas 坐标系下的"立牌内插口矩形"转成后端 slotRect 数组。
 * outlineOriginImageSrc 由 SKU 比例画布导出，slotRect 直接按图片实际宽高归一化。
 *
 * @param {Object} rectCanvas - { x, y, w, h }（canvas px，左上 + 宽高）
 * @param {Object} sharedState
 * @returns {Array<number>} slotRect: [x, y, width, height]（相对比例 0~1，保留 3 位小数）
 */
export function rectCanvasToSlotRect(rectCanvas, sharedState) {
  const beTopLeft = convertCanvasToBackend(
    { x: rectCanvas.x, y: rectCanvas.y },
    sharedState
  );
  const beWidth = Math.round(rectCanvas.w / sharedState.canvasScale);
  const beHeight = Math.round(rectCanvas.h / sharedState.canvasScale);
  const keep = 3;
  const x = Number((beTopLeft.x / sharedState.imageInfo.width).toFixed(keep));
  const y = Number((beTopLeft.y / sharedState.imageInfo.height).toFixed(keep));
  const width = Number((beWidth / sharedState.imageInfo.width).toFixed(keep));
  const height = Number(
    (beHeight / sharedState.imageInfo.height).toFixed(keep)
  );
  return [x, y, width, height];
}

/**
 * 合并后端坐标矩形边界。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @param {{left:number,top:number,right:number,bottom:number}} rect - 待合并矩形
 * @returns {void}
 */
function mergeBackendRect(bounds, rect) {
  bounds.minX = Math.min(bounds.minX, rect.left);
  bounds.minY = Math.min(bounds.minY, rect.top);
  bounds.maxX = Math.max(bounds.maxX, rect.right);
  bounds.maxY = Math.max(bounds.maxY, rect.bottom);
}

/**
 * 计算旋转矩形在后端像素坐标系下的 AABB。
 * @param {{x:number,y:number}} center - 后端像素坐标中心点
 * @param {number} width - 后端像素宽度
 * @param {number} height - 后端像素高度
 * @param {number} rotationRad - 旋转弧度
 * @returns {{left:number,top:number,right:number,bottom:number}}
 */
function getBackendRotatedRectAabb(center, width, height, rotationRad) {
  const cos = Math.cos(rotationRad || 0);
  const sin = Math.sin(rotationRad || 0);
  const halfW = width / 2;
  const halfH = height / 2;
  const local = [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH }
  ];
  const rect = {
    left: Infinity,
    top: Infinity,
    right: -Infinity,
    bottom: -Infinity
  };
  for (let i = 0; i < local.length; i++) {
    const x = center.x + local[i].x * cos - local[i].y * sin;
    const y = center.y + local[i].x * sin + local[i].y * cos;
    rect.left = Math.min(rect.left, x);
    rect.top = Math.min(rect.top, y);
    rect.right = Math.max(rect.right, x);
    rect.bottom = Math.max(rect.bottom, y);
  }
  return rect;
}

/**
 * 将组件实例转换为后端像素坐标下的 AABB。
 * @param {Object} inst - 组件实例
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {{left:number,top:number,right:number,bottom:number}|null}
 */
function getComponentBackendRect(inst, sharedState) {
  const k = sharedState.canvasScale || 1;
  // 缺少组件类型时无法计算组件区域，跳过该实例。
  if (!inst || !inst.type) return null;
  if (inst.type === DRAW_TYPE.KEY_HOLE && inst.centerPoint) {
    const center = convertCanvasToBackend(inst.centerPoint, sharedState);
    const radiusPx =
      typeof inst.outRadius === 'number'
        ? inst.outRadius
        : (((inst.geometryConfig && inst.geometryConfig.outerHoleDiameterMm) ||
            0) /
            2) *
          (sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1));
    const backendRadius = radiusPx / k;
    return {
      left: center.x - backendRadius,
      top: center.y - backendRadius,
      right: center.x + backendRadius,
      bottom: center.y + backendRadius
    };
  }
  if (inst.type === DRAW_TYPE.PERFORATION && inst.centerPoint) {
    const center = convertCanvasToBackend(inst.centerPoint, sharedState);
    const radiusPx =
      typeof inst.radius === 'number'
        ? inst.radius
        : ((inst.geometryConfig && inst.geometryConfig.radiusMm) || 0) *
          (sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1));
    const backendRadius = radiusPx / k;
    return {
      left: center.x - backendRadius,
      top: center.y - backendRadius,
      right: center.x + backendRadius,
      bottom: center.y + backendRadius
    };
  }
  if (inst.type === DRAW_TYPE.BASE_HOLE && inst.centerPoint) {
    const center = convertCanvasToBackend(inst.centerPoint, sharedState);
    const width = Math.round((inst.width || 0) / k);
    const height = Math.round((inst.height || 0) / k);
    return getBackendRotatedRectAabb(
      center,
      width,
      height,
      inst.rotationRad || 0
    );
  }
  if (inst.type === DRAW_TYPE.LI_PAI && inst.slotRect) {
    const s = inst.slotRect;
    const topLeft = convertCanvasToBackend({ x: s.x, y: s.y }, sharedState);
    const width = Math.round((s.w || 0) / k);
    const height = Math.round((s.h || 0) / k);
    return {
      left: topLeft.x,
      top: topLeft.y,
      right: topLeft.x + width,
      bottom: topLeft.y + height
    };
  }
  return null;
}

/**
 * 计算自定义尺寸组件导出使用的新后端坐标矩形。
 * @param {Array<Object>} components - 当前组件实例列表
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {{x:number,y:number,width:number,height:number}}
 */
export function getCustomSkuBackendBounds(components, sharedState) {
  const bounds = {
    minX: 0,
    minY: 0,
    maxX: sharedState.imageInfo.width,
    maxY: sharedState.imageInfo.height
  };
  const list = components || [];
  for (let i = 0; i < list.length; i++) {
    const rect = getComponentBackendRect(list[i], sharedState);
    if (!rect) continue;
    mergeBackendRect(bounds, rect);
  }
  return {
    x: bounds.minX,
    y: bounds.minY,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY
  };
}

/**
 * 计算自定义尺寸 SKU 的刀线后端坐标矩形。
 * outlineBounds 直接来自 imageBorderParse 返回的 rect；
 * componentOutlineBounds 包含 outline 与所有孔位组件外接范围。
 * @param {Array<Object>} holeRegions - 当前孔位区域
 * @param {Object} sharedState - 共享 canvas 状态
 * @returns {{outlineBounds:Object|null,baseOutlineBounds:Object|null,componentOutlineBounds:Object|null,basis:Object,imageBorderParseRect:Array<number>|null,baseImageBorderParseRect:Array<number>|null,imageSrc:string,imageInfo:Object|null}}
 */
export function getCustomSkuOutlineBounds(holeRegions, sharedState) {
  const outlineBounds = imageBorderParseRectToOutlineBounds(
    sharedState.imageBorderParseRect
  );
  const baseOutlineBounds = imageBorderParseRectToOutlineBounds(
    sharedState.baseImageBorderParseRect || sharedState.imageBorderParseRect
  );
  // 自定义尺寸 SKU 必须直接使用后台 rect 作为 outlineBounds 来源。
  if (!outlineBounds) {
    throw new Error('自定义尺寸同步失败：缺少 imageBorderParse 返回的 rect');
  }
  validateCustomSkuImagePaperRatio(sharedState);

  const componentBackendBounds = createBackendBounds();
  const outlinePointsBounds = mergeOutlinePointsToBackendBounds(
    componentBackendBounds,
    sharedState.outlinePoints,
    sharedState
  );
  mergeHoleRegionsToBackendBounds(
    componentBackendBounds,
    holeRegions,
    sharedState
  );

  // 组件区域矩形 = 刀线矩形 + 四边组件外扩增量（基于 outline 点集增量，加到 imageBorderParseRect 权威值上）
  let componentOutlineBounds = null;
  if (outlinePointsBounds && isValidBackendBounds(componentBackendBounds)) {
    const leftDelta = outlinePointsBounds.minX - componentBackendBounds.minX;
    const topDelta = outlinePointsBounds.minY - componentBackendBounds.minY;
    const rightDelta = componentBackendBounds.maxX - outlinePointsBounds.maxX;
    const bottomDelta = componentBackendBounds.maxY - outlinePointsBounds.maxY;
    componentOutlineBounds = {
      x: outlineBounds.x - leftDelta,
      y: outlineBounds.y - topDelta,
      width: outlineBounds.width + leftDelta + rightDelta,
      height: outlineBounds.height + topDelta + bottomDelta
    };
    console.log('[刀线+组件合并后] 矩形:', componentOutlineBounds);
  }

  return {
    outlineBounds,
    baseOutlineBounds,
    componentOutlineBounds:
      componentOutlineBounds || backendBoundsToRect(componentBackendBounds),
    imageBorderParseRect: sharedState.imageBorderParseRect,
    baseImageBorderParseRect: sharedState.baseImageBorderParseRect,
    imageSrc: sharedState.imageSrc,
    imageInfo: sharedState.imageInfo || null,
    basis: {
      imageWidth: sharedState.imageInfo && sharedState.imageInfo.width,
      imageHeight: sharedState.imageInfo && sharedState.imageInfo.height,
      erode: sharedState.erode,
      imageScale: sharedState.imageScale,
      canvasScale: sharedState.canvasScale,
      maskFitScale: sharedState.maskFitScale,
      mmToPixelRatio: sharedState.mmToPixelRatio,
      paperWidth:
        sharedState.imageBorderParseParams &&
        sharedState.imageBorderParseParams.paperWidth != null
          ? sharedState.imageBorderParseParams.paperWidth
          : sharedState.template && sharedState.template.paperWidth,
      paperHeight:
        sharedState.imageBorderParseParams &&
        sharedState.imageBorderParseParams.paperHeight != null
          ? sharedState.imageBorderParseParams.paperHeight
          : sharedState.template && sharedState.template.paperHeight,
      strokeWidth:
        sharedState.imageBorderParseParams &&
        sharedState.imageBorderParseParams.strokeWidth != null
          ? sharedState.imageBorderParseParams.strokeWidth
          : sharedState.strokeWidth
    }
  };
}

/**
 * 计算自定义尺寸 SKU 后端纸张坐标和 mm 的换算比例。
 * @param {Object} basis - 自定义尺寸矩形换算基准
 * @param {number} basis.imageWidth - 描边原图宽度基准
 * @param {number} basis.imageHeight - 描边原图高度基准
 * @param {number} basis.paperWidth - 纸张宽度，单位 mm
 * @param {number} basis.paperHeight - 纸张高度，单位 mm
 * @returns {{backendPaperWidth:number,backendPaperHeight:number,pxPerMmX:number,pxPerMmY:number}|null} 换算比例
 */
export function getCustomSkuBackendPaperScale(basis) {
  const imageWidth = Number(basis && basis.imageWidth);
  const imageHeight = Number(basis && basis.imageHeight);
  const paperWidth = Number(basis && basis.paperWidth);
  const paperHeight = Number(basis && basis.paperHeight);
  // 缺少换算基准时不能把后端坐标强行解释为 mm。
  if (!imageWidth || !imageHeight || !paperWidth || !paperHeight) {
    return null;
  }

  const imageRatio = imageWidth / imageHeight;
  const paperRatio = paperWidth / paperHeight;
  let backendPaperWidth;
  let backendPaperHeight;
  // 图片更宽时，后端纸张宽度以 imageInfo.width 作为基准。
  if (imageRatio > paperRatio) {
    backendPaperWidth = imageWidth;
    backendPaperHeight = imageWidth / paperRatio;
  } else {
    backendPaperHeight = imageHeight;
    backendPaperWidth = imageHeight * paperRatio;
  }

  return {
    backendPaperWidth,
    backendPaperHeight,
    pxPerMmX: backendPaperWidth / paperWidth,
    pxPerMmY: backendPaperHeight / paperHeight
  };
}

/**
 * 从含描边 outlineBounds 计算扣描边后的最小描边矩形。
 * @param {{outlineBounds:Object,baseOutlineBounds:Object,basis:Object}} customBounds - 自定义尺寸矩形信息
 * @returns {{x:number,y:number,width:number,height:number,strokeBackend:Object,scale:Object}|null} 后端坐标下的最小描边矩形
 */
export function getCustomSkuMinOutlineBounds(customBounds) {
  const outlineBounds =
    (customBounds && customBounds.baseOutlineBounds) ||
    (customBounds && customBounds.outlineBounds);
  const basis = customBounds && customBounds.basis;
  const scale = getCustomSkuBackendPaperScale(basis);
  // 缺少 outlineBounds 或换算基准时无法计算扣描边矩形。
  if (!outlineBounds || !scale) {
    return null;
  }

  const strokeWidth = Number(basis && basis.strokeWidth) || 0;
  const strokeBackendX = strokeWidth * scale.pxPerMmX;
  const strokeBackendY = strokeWidth * scale.pxPerMmY;
  const minOutlineBounds = {
    x: outlineBounds.x + strokeBackendX,
    y: outlineBounds.y + strokeBackendY,
    width: outlineBounds.width - strokeBackendX * 2,
    height: outlineBounds.height - strokeBackendY * 2,
    strokeBackend: {
      x: strokeBackendX,
      y: strokeBackendY
    },
    scale
  };
  // 扣描边后矩形必须仍然有效，否则说明 bounds 或描边参数异常。
  if (minOutlineBounds.width <= 0 || minOutlineBounds.height <= 0) {
    return null;
  }
  return minOutlineBounds;
}

/**
 * 立牌刀线变更后调用：把后端坐标系 outline 重新换算到 canvas 系，并更新
 * sharedState.outlinePoints / topHalfYArr / bottomHalfYArr。
 * 不重绘，调用方自行触发 update。
 *
 * @param {Object} sharedState
 * @param {Array<Array<number>>} newOutlineBackend - 后端坐标系新 outline
 * @returns {void}
 */
export function updateSharedOutlinePoints(sharedState, newOutlineBackend) {
  if (!newOutlineBackend || !Array.isArray(newOutlineBackend)) return;
  sharedState.outlinePoints = newOutlineBackend.map(function (p) {
    return [
      Math.round(p[0] * sharedState.canvasScale),
      Math.round(p[1] * sharedState.canvasScale)
    ];
  });
  const median = splitByMedianY(sharedState.outlinePoints);
  sharedState.topHalfYArr = median.topHalfYArr;
  sharedState.bottomHalfYArr = median.bottomHalfYArr;
}

// 透出 DRAW_TYPE 给本模块的某些 type 判定方便引用
export { DRAW_TYPE };
